import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { GettingStartedFacade } from '@core/getting-started-legacy';
import { GettingStartedSubtask } from '@core/getting-started-types';
import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  globalPositionStrategyFactory,
  Step,
  WalkthroughDialogComponent,
  WalkthroughFacadeService,
  WalkthroughNavigationHelperService,
  WalkthroughPostComponent,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';
import { walkthroughAssetsResolver } from '@core/walkthrough-util';

@Injectable({ providedIn: 'root' })
export class BrowseJobWalkthroughService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);
  private readonly navigationHelperService = inject(WalkthroughNavigationHelperService);
  private readonly walkthroughFacadeService = inject(WalkthroughFacadeService);
  private readonly gettingStartedFacade = inject(GettingStartedFacade);

  private readonly destroyRef = inject(DestroyRef);

  public readonly subtask = GettingStartedSubtask.BrowsePublishedJobs;

  private steps: Step[] = [
    {
      id: 'walkthrough-step-browse-jobs-1',
      position: 1,
      component: {
        definition: WalkthroughDialogComponent,
        inputs: {
          heading: this.i18n.translate('app.walkthrough.walkthroughs.browse_job.step_1.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.browse_job.step_1.description',
          ),
          imageUrl: walkthroughAssetsResolver('browse_job.svg'),
          ctaText: this.i18n.translate('app.walkthrough.walkthroughs.browse_job.step_1.cta_text'),
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
    },
    {
      id: 'walkthrough-step-browse-jobs-2',
      position: 2,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'right'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.browse_job.step_2.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.browse_job.step_2.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [
          { originX: 'center', originY: 'center', overlayX: 'center', overlayY: 'bottom' },
        ],
        offset: { offsetX: 200, offsetY: 400 },
      },
    },
    {
      id: 'walkthrough-step-browse-jobs-3',
      position: 3,

      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'bottom'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.browse_job.step_3.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.browse_job.step_3.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'top' }],
        offset: { offsetX: 0, offsetY: -140 },
      },
    },
    {
      id: 'walkthrough-step-browse-jobs-4',
      position: 4,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['left', 'top'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.browse_job.step_4.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.browse_job.step_4.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'bottom', overlayX: 'start', overlayY: 'top' }],
        offset: { offsetX: 0, offsetY: 24 },
      },
    },
    {
      id: 'walkthrough-step-browse-jobs-5',
      position: 5,

      component: {
        definition: WalkthroughPostComponent,
        inputs: {
          heading: this.i18n.translate('app.walkthrough.walkthroughs.browse_job.step_5.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.browse_job.step_5.description',
          ),
          ctaText: this.i18n.translate('app.walkthrough.walkthroughs.browse_job.step_5.cta_text'),
          ctaIcon: 'open-in-new',
        },
      },
      renderOptions: {
        strategy: globalPositionStrategyFactory,
      },
      finishHandler: () => {
        this.finishHandler();
      },
    },
  ];

  public start(): void {
    this.walkthroughStepsService
      .start(this.steps)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        this.navigationHelperService.handleWalkthroughState(state, this.subtask, ['offers']);
      });
    this.walkthroughStepsService.onLastStep$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.gettingStartedFacade.markSubtaskAsDone(this.subtask);
      });
  }

  public navigateToNextStep(step: Step['id'] | null): void {
    // eslint-disable-next-line default-case
    switch (step) {
      case 'walkthrough-step-browse-jobs-3':
        this.router.navigate(['/offers', 'published', 'pipeline']);
        break;
    }
  }
  private finishHandler(): void {
    this.walkthroughFacadeService.openHelpArticleLink('notificationsPerRole');
    this.navigationHelperService.navigateAndOpenDrawer(['offers']);
  }
}
